import type { Conversation, FolderId } from '@/types';
import { useAuthStore } from '@/stores/authStore';

/**
 * The conversation list's lifecycle tabs (conversation-status overhaul): All / Need help / Bot handling / Agent handling.
 * The choice is sent to the server as the `lifecycle` query param (`GET /api/sessions` and `GET /api/sessions/counts`),
 * which applies it on top of ANY folder:
 *
 *   need-help = state CONSULTING          (the console's CONSULTATION_REQUESTED)
 *   bot       = state NEW or ACTIVE       (the console's AI_HANDLING)
 *   agent     = state HUMAN_CONTROLLED    (the console's HUMAN_TAKEOVER)
 *
 * {@link lifecycleMatches} is the CLIENT COPY, used only to re-bucket a row a push just changed between server pages.
 * The server list is the authority on what a tab lists.
 */
export type LifecycleTab = 'all' | 'need-help' | 'bot' | 'agent';

export const LIFECYCLE_TABS: readonly LifecycleTab[] = ['all', 'need-help', 'bot', 'agent'];
export const DEFAULT_LIFECYCLE_TAB: LifecycleTab = 'all';

export const isLifecycleTab = (v: unknown): v is LifecycleTab =>
  typeof v === 'string' && (LIFECYCLE_TABS as readonly string[]).includes(v);

/** Does this row belong to the tab? `all` matches every row. Uses the console's own state names (types SessionState). */
export function lifecycleMatches(tab: LifecycleTab, c: Pick<Conversation, 'state'>): boolean {
  switch (tab) {
    case 'need-help':
      return c.state === 'CONSULTATION_REQUESTED';
    case 'bot':
      return c.state === 'AI_HANDLING';
    case 'agent':
      return c.state === 'HUMAN_TAKEOVER';
    default:
      return true;
  }
}

/**
 * The key a folder's page cache lives under. The `all` tab keeps the bare folder id; any other tab appends `@<tab>`, so
 * switching tabs never shows rows (or a cursor) from another tab. A search is not a folder: the tab never applies to it.
 */
export function folderPageKey(folder: FolderId, tab: LifecycleTab): string {
  if (tab === 'all' || folder.startsWith('search:')) return folder;
  return `${folder}@${tab}`;
}

/** Does the tab apply to this folder id (a list folder that is not a search)? The drafts feed has no list call at all. */
export const tabAppliesTo = (folder: FolderId): boolean => folder !== 'drafts' && !folder.startsWith('search:');

// ── Per-viewer memory ────────────────────────────────────────────────────────────────────────────────────────────

export const LIFECYCLE_STORAGE_PREFIX = 'ac-list-lifecycle:';
const storageKey = (agentId: string | null | undefined): string => `${LIFECYCLE_STORAGE_PREFIX}${agentId ?? ''}`;

/** The tab this viewer chose last, or `all`. Storage that is unavailable or holds junk reads as `all`. */
export function readStoredLifecycleTab(agentId?: string | null): LifecycleTab {
  const who = agentId === undefined ? useAuthStore.getState().user?.agentId : agentId;
  try {
    const raw = localStorage.getItem(storageKey(who));
    return isLifecycleTab(raw) ? raw : DEFAULT_LIFECYCLE_TAB;
  } catch {
    return DEFAULT_LIFECYCLE_TAB;
  }
}

/** Remember the viewer's choice. Never throws (private mode, quota). */
export function writeStoredLifecycleTab(tab: LifecycleTab, agentId?: string | null): void {
  const who = agentId === undefined ? useAuthStore.getState().user?.agentId : agentId;
  try {
    localStorage.setItem(storageKey(who), tab);
  } catch {
    /* a convenience, not a requirement */
  }
}
